<script>
import { layoutMethods, puhunanLoansMethods, puhunanLoansComputed } from "@/state/helpers";
import moment from "moment";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import Swal from "sweetalert2";

// KPI cards (Figma "Puhunan Disbursed", node 4504:9015). `sub` renders the small
// line under the value; `subTone` colors it (up = green, brand = purple, etc.).
const CARDS = [
  {
    key: "totalDisbursedAmount",
    title: "Total Disbursed Amount",
    icon: "mdi-cash-multiple",
    format: "compactPeso",
    subText: () => "Total value of successfully disbursed loans.",
  },
  {
    key: "successfullyDisbursed",
    title: "Successfully Disbursed",
    icon: "mdi-check-decagram",
    iconTone: "success",
    format: "loans",
    subText: () => "Loans with successful disbursement.",
  },
  {
    key: "completedLoans",
    title: "Completed Loans",
    // Custom icon from the design (rendered as inline SVG, not an mdi glyph).
    iconSvg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 6H4V4H20V6ZM15.69 14H14V15.69C13.37 16.64 13 17.77 13 19C13 19.34 13.04 19.67 13.09 20H4V14H3V12L4 7H20L21 12V13.35C20.37 13.13 19.7 13 19 13C17.77 13 16.64 13.37 15.69 14ZM12 14H6V18H12V14ZM21.34 15.84L17.75 19.43L16.16 17.84L15 19L17.75 22L22.5 17.25L21.34 15.84Z" fill="#2563EB"/></svg>`,
    format: "loans",
    subText: () => "Loans with a completed date.",
  },
  {
    key: "averageLoanAmount",
    title: "Average Loan Amount",
    // Custom icon from the design (rendered as inline SVG, not an mdi glyph).
    iconSvg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 20H16V22H4C2.9 22 2 21.1 2 20V7H4M22 4V16C22 17.1 21.1 18 20 18H8C6.9 18 6 17.1 6 16V4C6 2.9 6.9 2 8 2H20C21.1 2 22 2.9 22 4ZM12 8H10V14H12M15 6H13V14H15M18 11H16V14H18V11Z" fill="#D32C85"/></svg>`,
    format: "peso",
    subText: () => "Average per successfully disbursed loan.",
  },
];

// "Filter by Status" -> ref_puhunan_loans.status enum (only active/inactive exist).
const STATUS_OPTIONS = [
  { value: "", text: "All" },
  { value: "active", text: "Active" },
  { value: "inactive", text: "Inactive" },
];

// "Search All" dropdown — scopes the keyword to one column, or all searchable
// columns. Values match the API's SEARCH_FIELDS whitelist.
const SEARCH_FIELD_OPTIONS = [
  { value: "all", text: "Search All" },
  { value: "loanId", text: "Loan ID" },
  { value: "merchantId", text: "Merchant ID" },
  { value: "merchantName", text: "Merchant Name" },
];

// b-table fields (house pattern). `type` drives the cell-formatting template.
// merchantName is unsortable: it's resolved from other DBs after the query.
const COLUMNS = [
  { key: "loanId", label: "Loan ID", sortable: true },
  { key: "merchantId", label: "Merchant ID", sortable: true },
  { key: "merchantName", label: "Merchant Name", sortable: false, thClass: "po-th-nosort" },
  { key: "loanAmount", label: "Loan Amount", sortable: true, type: "peso", thClass: "text-end", tdClass: "text-end" },
  { key: "status", label: "Status", sortable: true, type: "status" },
  { key: "disbursementDate", label: "Disbursement Date", sortable: true, type: "date" },
];

export default {
  page: {
    title: "Puhunan Disbursed",
  },
  components: {
    flatPickr,
  },
  data() {
    return {
      isLoading: false,
      cardDefs: CARDS,
      columns: COLUMNS,
      statusOptions: STATUS_OPTIONS,
      searchFieldOptions: SEARCH_FIELD_OPTIONS,
      // filter inputs
      searchField: "all",
      searchKeyword: "",
      status: "",
      dateRange: "",
      // Single range input, e.g. "2023-02-01 to 2023-03-01".
      rangeConfig: {
        mode: "range",
        dateFormat: "Y-m-d",
        maxDate: moment().format("YYYY-MM-DD"),
        // Range mode leaves the input blank when only one date is picked and the
        // popup closes. Complete it as a same-day range so the field shows the
        // selection (and v-model stays in sync with what's sent to the API).
        onClose: (selectedDates, _dateStr, instance) => {
          if (selectedDates.length === 1) {
            instance.setDate([selectedDates[0], selectedDates[0]], true);
          }
        },
      },
      // applied filter (what the table reflects)
      applied: { search: "", searchField: "all", status: "", from: "", to: "" },
      currentPage: 1,
      perPage: 6,
      pageOptions: [6, 10, 20, 50, 100],
      // Server-side sort: sortBy is the column key sent to the API, orderDesc the direction.
      sortBy: "loanId",
      sortDesc: true,
      isExporting: false,
      hasLoadedOnce: false,
    };
  },
  computed: {
    ...puhunanLoansComputed,

    kpi() {
      return this.loanKpi || {};
    },
    rows() {
      return this.loanRows || [];
    },
    totalRecords() {
      return this.loanPageInfo?.total || 0;
    },
    cardList() {
      const k = this.kpi || {};
      return this.cardDefs.map((def) => ({
        ...def,
        value: k[def.key],
        sub: def.subText(k),
      }));
    },
  },
  methods: {
    ...layoutMethods,
    ...puhunanLoansMethods,

    formatNumber(value) {
      if (value === null || value === undefined || value === "") return "0";
      return new Intl.NumberFormat("en-US").format(value);
    },
    // Whole pesos, no decimals — e.g. "PHP 2,000".
    formatPeso(value) {
      return `PHP ${new Intl.NumberFormat("en-US").format(Math.round(Number(value) || 0))}`;
    },
    // Abbreviated for the KPI card: 18700000 -> "PHP 18.7M".
    formatCompactPeso(value) {
      const n = Number(value) || 0;
      if (Math.abs(n) >= 1e9) return `PHP ${(n / 1e9).toFixed(1)}B`;
      if (Math.abs(n) >= 1e6) return `PHP ${(n / 1e6).toFixed(1)}M`;
      if (Math.abs(n) >= 1e3) return `PHP ${(n / 1e3).toFixed(1)}K`;
      return this.formatPeso(n);
    },
    // Count cards read "4,200 Loans" per the design.
    formatLoans(value) {
      return `${this.formatNumber(value)} Loans`;
    },
    formatCardValue(card) {
      const v = card.value ?? 0;
      if (card.format === "compactPeso") return this.formatCompactPeso(v);
      if (card.format === "peso") return this.formatPeso(v);
      if (card.format === "loans") return this.formatLoans(v);
      return this.formatNumber(v);
    },
    capitalize(s) {
      const str = String(s || "");
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    // Disbursement Date display, e.g. "January 15, 2026 at 02:30 PM".
    formatDate(v) {
      if (!v) return "--";
      const m = moment(v);
      return m.isValid() ? m.format("MMMM D, YYYY [at] hh:mm A") : v;
    },
    // Status is coloured text in the design, not a pill.
    statusClass(v) {
      return String(v || "").toLowerCase() === "active" ? "po-status-active" : "po-status-inactive";
    },
    // Server-side sort: b-table emits ctx with sortBy/sortDesc; jump to page 1 and refetch.
    // merchantName is resolved from other DBs after the query, so it can't be sorted in SQL — ignore it.
    onSortChanged(ctx) {
      if (ctx.sortBy === "merchantName") return;
      this.sortBy = ctx.sortBy;
      this.sortDesc = ctx.sortDesc;
      this.currentPage = 1;
      this.fetchList();
    },
    // flatpickr range mode emits "YYYY-MM-DD to YYYY-MM-DD" (single date until
    // the second is picked), so split on " to " and reuse from for both.
    splitRange() {
      if (!this.dateRange) return { from: "", to: "" };
      const [from, to] = this.dateRange.split(" to ");
      return { from: from || "", to: to || from || "" };
    },

    async fetchList() {
      this.isLoading = true;
      try {
        const res = await this.getPuhunanLoansList({
          page: this.currentPage,
          rows: this.perPage,
          search: this.applied.search || undefined,
          searchField: this.applied.search ? this.applied.searchField : undefined,
          status: this.applied.status || undefined,
          from: this.applied.from || undefined,
          to: this.applied.to || undefined,
          sort: this.sortBy,
          orderDesc: this.sortDesc,
        });
        if (res && res.status !== "ok") {
          Swal.fire({ icon: "error", title: "Unable to load loans", text: res.message });
        }
      } catch (error) {
        console.error("Failed to load Puhunan disbursed loans:", error);
        Swal.fire({ icon: "error", title: "Unable to load loans", text: "Please try again." });
      } finally {
        this.isLoading = false;
        this.hasLoadedOnce = true;
      }
    },
    async fetchKpi() {
      try {
        const res = await this.getPuhunanLoansKPI({});
        if (res && res.status !== "ok") console.warn("KPI load returned:", res.message);
      } catch (error) {
        console.error("Failed to load Puhunan disbursed KPI:", error);
      }
    },
    applyFilter() {
      const { from, to } = this.splitRange();
      this.applied = {
        search: this.searchKeyword.trim(),
        searchField: this.searchField,
        status: this.status,
        from,
        to,
      };
      this.currentPage = 1;
      this.fetchList();
    },
    // Clears every filter and reloads.
    refresh() {
      this.searchField = "all";
      this.searchKeyword = "";
      this.status = "";
      this.dateRange = "";
      this.applied = { search: "", searchField: "all", status: "", from: "", to: "" };
      this.sortBy = "loanId";
      this.sortDesc = true;
      this.currentPage = 1;
      this.fetchKpi();
      this.fetchList();
    },
    onPerPageChange() {
      this.currentPage = 1;
      this.fetchList();
    },
    // Exports the full filtered set (all pages) — the API emails an xlsx link.
    async onExport() {
      if (this.isExporting) return;
      if (!this.totalRecords) {
        Swal.fire({ icon: "info", title: "Nothing to export", text: "No records for the selected filters." });
        return;
      }

      // The API emails the xlsx to the logged-in user (same pattern as other exports).
      let email;
      try {
        email = JSON.parse(localStorage.getItem("userData"))?.email;
      } catch (e) {
        email = null;
      }
      if (!email) {
        Swal.fire({ icon: "error", title: "Export failed", text: "Could not determine your email. Please re-login and try again." });
        return;
      }

      this.isExporting = true;
      try {
        const res = await this.exportPuhunanLoans({
          email,
          search: this.applied.search || undefined,
          searchField: this.applied.search ? this.applied.searchField : undefined,
          status: this.applied.status || undefined,
          from: this.applied.from || undefined,
          to: this.applied.to || undefined,
          sort: this.sortBy,
          orderDesc: this.sortDesc,
        });
        if (res && res.status === "ok") {
          Swal.fire({ icon: "success", title: "Export started", text: res.message });
        } else {
          Swal.fire({ icon: "error", title: "Export failed", text: (res && res.message) || "Please try again." });
        }
      } catch (error) {
        console.error("Failed to export Puhunan disbursed loans:", error);
        Swal.fire({ icon: "error", title: "Export failed", text: "Please try again." });
      } finally {
        this.isExporting = false;
      }
    },
  },
  async mounted() {
    this.changeTitle({ title: "Puhunan Disbursed" });
    this.changeBreadCrumb({
      breadCrumbs: [{ text: "Peddlr" }, { text: "Reports" }, { text: "Puhunan Disbursed" }],
    });
    await Promise.all([this.fetchKpi(), this.fetchList()]);
  },
};
</script>

<template>
  <div class="container-fluid po-page">
    <!-- KPI Cards -->
    <div class="po-bento mb-3">
      <div v-for="card in cardList" :key="card.key" class="po-card po-metric">
        <div class="po-metric-head">
          <span class="po-metric-title">{{ card.title }}</span>
          <!-- eslint-disable-next-line vue/no-v-html -- static, hard-coded SVG from the design (no user input) -->
          <span v-if="card.iconSvg" class="po-metric-icon po-metric-icon-svg" aria-hidden="true" v-html="card.iconSvg"></span>
          <i v-else :class="['mdi', card.icon, 'po-metric-icon', card.iconTone ? 'is-' + card.iconTone : '']" aria-hidden="true"></i>
        </div>
        <div class="po-metric-value">
          <span v-if="isLoading && !hasLoadedOnce" class="po-skeleton"></span>
          <template v-else>{{ formatCardValue(card) }}</template>
        </div>
        <div class="po-metric-sub">
          <span>{{ card.sub }}</span>
        </div>
      </div>
    </div>

    <!-- Records -->
    <div class="po-card">
      <!-- header: record count + refresh -->
      <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <h5 class="po-records-count mb-0">{{ formatNumber(totalRecords) }} Record(s)</h5>
        <button class="po-btn-refresh" :disabled="isLoading" @click="refresh">
          <i :class="isLoading ? 'bx bx-loader-alt bx-spin' : 'mdi mdi-refresh'"></i>
          <span>Refresh</span>
        </button>
      </div>

      <!-- filter row: split keyword search + status + date range + search -->
      <div class="po-filter-row">
        <div class="po-field">
          <div class="po-search-group">
            <b-form-select
              v-model="searchField"
              :options="searchFieldOptions"
              value-field="value"
              text-field="text"
              class="form-select rounded-0 rounded-start shadow-none po-search-select"
              aria-label="Search field"
            ></b-form-select>
            <input
              id="po-search-keyword"
              v-model="searchKeyword"
              type="text"
              class="po-search-input"
              placeholder=""
              aria-label="Search keyword"
              @keyup.enter="applyFilter"
            />
          </div>
          <span class="po-field-sub">Search Keyword</span>
        </div>

        <div class="po-field">
          <b-form-select
            v-model="status"
            :options="statusOptions"
            value-field="value"
            text-field="text"
            class="form-select shadow-none po-select"
            aria-label="Filter by status"
          ></b-form-select>
          <span class="po-field-sub">Filter by Status</span>
        </div>

        <div class="po-field">
          <flat-pickr
            v-model="dateRange"
            :config="rangeConfig"
            class="po-date-field"
            placeholder="Select date range"
            aria-label="Filter by disbursement date"
          />
          <span class="po-field-sub">Filter by Disbursement Date</span>
        </div>

        <div class="po-field">
          <button class="po-btn-search" :disabled="isLoading" @click="applyFilter">
            <i class="mdi mdi-magnify"></i>
            <span>Search</span>
          </button>
        </div>
      </div>

      <!-- export -->
      <div class="mb-3">
        <button class="po-btn-export" :disabled="isExporting || isLoading" @click="onExport">
          <i :class="isExporting ? 'bx bx-loader-alt bx-spin' : 'bx bx-upload'"></i>
          <span>{{ isExporting ? 'Exporting…' : 'Export' }}</span>
        </button>
      </div>

      <div class="table-responsive po-table-scroll">
        <div class="table align-middle table-nowrap">
          <b-table
            :items="rows"
            :fields="columns"
            :busy="isLoading"
            responsive="sm"
            :current-page="currentPage"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :no-local-sorting="true"
            show-empty
            class="po-table table-check"
            @sort-changed="onSortChanged"
          >
            <template #table-busy>
              <div class="po-state">
                <i class="bx bx-loader-alt bx-spin me-2"></i>Loading loans…
              </div>
            </template>

            <template #empty>
              <div class="po-state">
                <i class="mdi mdi-file-search-outline me-2"></i>
                No disbursed loans found for the selected filters.
              </div>
            </template>

            <template #cell(merchantName)="data">
              <p class="text-body m-0">{{ data.item.merchantName || '--' }}</p>
            </template>

            <template #cell(loanAmount)="data">
              <p class="text-body m-0 text-end">{{ formatPeso(data.item.loanAmount) }}</p>
            </template>

            <template #cell(status)="data">
              <span :class="statusClass(data.item.status)">
                {{ data.item.status ? capitalize(data.item.status) : '--' }}
              </span>
            </template>

            <template #cell(disbursementDate)="data">
              <p class="text-body m-0">{{ formatDate(data.item.disbursementDate) }}</p>
            </template>
          </b-table>
        </div>
      </div>

      <!-- Footer: display size + pagination (shared BootstrapVue controls) -->
      <div class="d-flex align-items-center justify-content-between mt-3 flex-wrap gap-2">
        <div class="d-flex align-items-center gap-2">
          <label for="po-display-record" class="po-display-label mb-0">Display</label>
          <b-form-select
            id="po-display-record"
            class="form-select shadow-none w-fit po-display-select"
            v-model="perPage"
            :options="pageOptions"
            @change="onPerPageChange"
          ></b-form-select>
        </div>

        <b-pagination
          v-if="totalRecords > perPage"
          class="pagination-rounded mb-0"
          v-model="currentPage"
          :total-rows="totalRecords"
          :per-page="perPage"
          @input="fetchList"
        ></b-pagination>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* 
   primary #7F4697 / primary-600 #744089 | secondary #E83092 / secondary-700 #A52268
   success-700 #396622 | neutral-300 #868686 | neutral-400 #AFAFAF
   neutral-500 #4B4B4B | neutral-800 #191919 | bg-tertiary #E6EAF1 | neutral-200 #E1E1E1 */
.po-page {
  --po-primary: #7f4697;
  --po-primary-600: #744089;
  --po-secondary: #e83092;
  --po-secondary-700: #a52268;
  --po-success: #396622;
  --po-muted: #868686;
  --po-neutral-400: #afafaf;
  --po-neutral-500: #4b4b4b;
  --po-neutral-800: #191919;
  --po-tertiary: #e6eaf1;
  --po-border: #e1e1e1;
}

.po-card {
  background: #fff;
  border: 1px solid var(--po-border);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

/* ---------- KPI cards: 4 across ---------- */
.po-bento {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.po-metric {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.po-metric-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.po-metric-title {
  font-size: 14px;
  font-weight: 400;
  color: var(--po-neutral-500);
  line-height: 20px;
}

.po-metric-icon {
  font-size: 22px;
  line-height: 1;
  color: var(--po-secondary-700);
  flex-shrink: 0;
}
.po-metric-icon.is-success { color: var(--po-success); }

/* Inline-SVG variant: match the mdi glyph box (~22px) and keep it top-aligned. */
.po-metric-icon-svg {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.po-metric-icon-svg ::v-deep svg {
  width: 22px;
  height: 22px;
  display: block;
}

.po-metric-value {
  font-size: 32px;
  font-weight: 700;
  line-height: 32px;
  color: var(--po-neutral-800);
}

/* Figma: sub-text is neutral-500, no leading icon, regular weight. */
.po-metric-sub {
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: var(--po-neutral-500);
}

/* ---------- Filter row (sub-labels centred beneath, per Figma) ---------- */
.po-filter-row {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.po-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.po-field-sub {
  font-size: 12px;
  color: var(--po-neutral-500);
  text-align: right;
}

/* Split search: field selector fused to the keyword input */
.po-search-group {
  display: inline-flex;
  align-items: stretch;
}

/* Field selector ("Search All") fused to the input. b-form-select's `form-select`
   class supplies the padded caret; we only override sizing + the fused-left border. */
.po-search-select {
  width: auto;
  border: 1px solid var(--po-border);
  border-right: none;
  height: 40px;
  font-size: 14px;
  color: var(--po-neutral-800);
}
.po-search-select:focus {
  border-color: var(--po-primary);
  box-shadow: 0 0 0 0.15rem rgba(127, 70, 151, 0.12);
  position: relative;
  z-index: 1;
}

.po-search-input {
  border: 1px solid var(--po-border);
  border-radius: 0 4px 4px 0;
  background: #fff;
  padding: 0 14px;
  height: 40px;
  font-size: 14px;
  color: var(--po-neutral-800);
  width: 200px;
}

.po-search-input:focus,
.po-date-field:focus {
  outline: none;
  border-color: var(--po-primary);
  box-shadow: 0 0 0 0.15rem rgba(127, 70, 151, 0.12);
  position: relative;
  z-index: 1;
}

/* Status filter. `form-select` supplies the padded caret; we set the design's
   height, border colour and a min width to match the mock. */
.po-select {
  border: 1px solid var(--po-border);
  height: 40px;
  font-size: 14px;
  color: var(--po-neutral-800);
  min-width: 180px;
}
.po-select:focus {
  border-color: var(--po-primary);
  box-shadow: 0 0 0 0.15rem rgba(127, 70, 151, 0.12);
}

/* Single range input, matching "2023-02-01 to 2023-03-01" */
.po-date-field {
  border: 1px solid var(--po-border);
  border-radius: 4px;
  background: #fff;
  padding: 0 14px;
  height: 40px;
  font-size: 14px;
  color: var(--po-neutral-800);
  width: 210px;
  cursor: pointer;
}

.po-btn-search {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--po-secondary);
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 0 20px;
  height: 40px;
  font-size: 14px;
  font-weight: 500;
}
.po-btn-search:hover:not(:disabled) { background: #d02782; }
.po-btn-search:disabled { opacity: 0.65; }
.po-btn-search i { font-size: 18px; line-height: 1; }

.po-btn-refresh {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--po-primary);
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 0 18px;
  height: 40px;
  font-size: 14px;
  font-weight: 500;
}
.po-btn-refresh:hover:not(:disabled) { background: var(--po-primary-600); }
.po-btn-refresh:disabled { opacity: 0.65; }
.po-btn-refresh i { font-size: 18px; line-height: 1; }

/* ---------- Records header ---------- */
.po-records-count {
  font-size: 20px;
  font-weight: 600;
  color: var(--po-neutral-800);
}

.po-btn-export {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--po-secondary);
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 0 20px;
  height: 40px;
  font-size: 14px;
  font-weight: 500;
}
.po-btn-export:hover:not(:disabled) { background: #d02782; }
.po-btn-export:disabled { opacity: 0.7; cursor: not-allowed; }
.po-btn-export i { font-size: 18px; line-height: 1; }

/* ---------- Table ---------- */
.po-table-scroll {
  overflow-x: auto;
  padding-bottom: 6px;
  scrollbar-width: thin;
  scrollbar-color: #c9c9c9 #f1f1f1;
}
.po-table-scroll::-webkit-scrollbar { height: 6px; }
.po-table-scroll::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 3px; }
.po-table-scroll::-webkit-scrollbar-thumb { background: #c9c9c9; border-radius: 3px; }
.po-table-scroll::-webkit-scrollbar-thumb:hover { background: #b0b0b0; }

.po-table { width: 100%; margin: 0; }

.po-table ::v-deep th {
  font-size: 13px;
  font-weight: 700;
  color: var(--po-neutral-800);
  padding: 12px 16px;
  border-bottom: 1px solid var(--po-tertiary);
  white-space: nowrap;
  background: #fff;
}

.po-table ::v-deep td {
  font-size: 13px;
  font-weight: 400;
  color: var(--po-neutral-800);
  padding: 18px 16px;
  border-bottom: 1px solid var(--po-tertiary);
  white-space: nowrap;
}
.po-table ::v-deep tbody tr:hover td { background: #faf8fb; }

/* Merchant Name is not sortable (resolved after the query) — never show a sort caret or pointer. */
.po-table ::v-deep th.po-th-nosort { cursor: default; }
.po-table ::v-deep th.po-th-nosort > div[role="columnheader"],
.po-table ::v-deep th.po-th-nosort { background-image: none !important; }
.po-table ::v-deep th.po-th-nosort::before,
.po-table ::v-deep th.po-th-nosort::after { display: none !important; }

/* Status as coloured text (design shows no pill) */
.po-status-active { color: var(--po-success); font-weight: 500; }
.po-status-inactive { color: var(--po-muted); }

.po-state {
  color: var(--po-neutral-500);
  font-size: 13px;
  text-align: center;
  padding: 40px 20px !important;
}

.po-skeleton {
  display: inline-block;
  width: 96px;
  height: 30px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0ecf2 25%, #e6e0ea 37%, #f0ecf2 63%);
  background-size: 400% 100%;
  animation: po-shimmer 1.2s ease-in-out infinite;
}
@keyframes po-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

/* ---------- Footer ---------- */
.po-display-label {
  font-size: 13px;
  font-weight: 700;
  color: var(--po-neutral-800);
}

.po-display-select { width: auto; min-width: 72px; }

/* Tint the shared b-pagination active page to the Puhunan primary */
.po-page ::v-deep .pagination-rounded .page-item.active .page-link {
  background-color: var(--po-primary);
  border-color: var(--po-primary);
}

@media (max-width: 1399px) {
  .po-bento { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 767px) {
  .po-bento { grid-template-columns: 1fr; }
}
</style>
